import { createSlice } from '@reduxjs/toolkit';

import { ODS_SORT_PROPERTY, subClass, updateDateType } from '@/constants';
import type {
  Spot,
  SpotTypeMarkerMeta,
  SpotTypeDetailMeta,
  SpotTypeItem,
  SpotTag,
  IntervalSpotTypes,
  LinkageLayerTypeIds,
} from '@/types';

export interface SpotsState {
  spotTypeMarkerMeta: SpotTypeMarkerMeta;
  spotDetailMeta: SpotTypeDetailMeta;
  spots: Spot[];
  spotTypes: SpotTypeItem[];
  spotTags: SpotTag[];
  mapTypeFlg: string;
  groupFlgs: string[] | null;
  defaultGroupFlgs: string[] | null;
  intervalSpotTypes: IntervalSpotTypes[];
  linkageLayerTypeIds: LinkageLayerTypeIds;
}

const initialState: SpotsState = {
  spotTypeMarkerMeta: {},
  spotDetailMeta: {},
  spots: [],
  spotTypes: [],
  spotTags: [],
  mapTypeFlg: '',
  groupFlgs: null,
  defaultGroupFlgs: null,
  intervalSpotTypes: [],
  linkageLayerTypeIds: {},
};

export const spotSlice = createSlice({
  name: 'spot',
  initialState,
  reducers: {
    setMapTypeFlg: (state, action) => {
      state.mapTypeFlg = action.payload;
    },
    setSpots: (state, { payload }) => {
      const spots = payload as Spot[];
      spots.forEach((spot, index) => {
        if (spot.attributes?.kana !== undefined) {
          state.spots[index] = {
            ...spot,
            kana: spot.attributes?.kana as string,
          };
        } else if (spot.attributes && ODS_SORT_PROPERTY in spot.attributes) {
          const obj = spot.attributes[ODS_SORT_PROPERTY];
          if (typeof obj === 'object') {
            state.spots[index] = {
              ...spot,
              kana: obj.text as string,
            };
          }
        } else {
          state.spots[index] = {
            ...spot,
            kana: '',
          };
        }
        if (spot.attributes?.index !== undefined) {
          if (typeof spot.attributes?.index === 'number') {
            state.spots[index] = {
              ...state.spots[index],
              index: spot.attributes?.index as number,
            };
          } else if (typeof spot.attributes?.index === 'string') {
            state.spots[index] = {
              ...state.spots[index],
              index: parseInt(spot.attributes?.index ?? '0'),
            };
          }
        } else {
          state.spots[index] = {
            ...state.spots[index],
            index: -1,
          };
        }
      });
    },
    setSpotTypes: (state, { payload }) => {
      state.spotTypes = payload;
    },
    setSpotTags: (state, { payload }) => {
      state.spotTags = payload;
    },
    setGroupFlgs: (state, { payload }) => {
      state.groupFlgs = payload;
    },
    setDefaultGroupFlgs: (state, { payload }) => {
      state.defaultGroupFlgs = payload;
    },
    setMarkerMeta: (state, action) => {
      const assetTypeId = action.payload.assetTypeId;
      const spotTypeMarkerMeta = state.spotTypeMarkerMeta;
      const classification = action.payload.assetType.attributes.classification;
      const _subClass = action.payload.assetType.attributes.subClass;
      const updateDataType = action.payload.assetType.attributes.updateDataType;
      const setMarkerInfo = (imagePath: string) => {
        const spotPopupType = action.payload.assetType.attributes.spotPopupType;
        const detailIconView =
          action.payload.assetType.attributes.detailIconView;
        spotTypeMarkerMeta[assetTypeId] = {
          spotPopupType,
          imagePath,
          detailIconView,
          updateDataType,
          subClass: _subClass,
        };
      };

      if (classification === 'spot') {
        // TODO 給水ポータルもupdateDataTypeで判断するようにしたい
        if (
          _subClass === subClass.WATER_SUPPLY ||
          _subClass === subClass.ICON_WITH_STATUS ||
          updateDataType === updateDateType.OBSERVATION_SCRAPING
        ) {
          setMarkerInfo(action.payload.assetType.attributes.images);
        } else {
          setMarkerInfo(action.payload.assetType.attributes.images[0].url);
        }
      }
    },
    setDetailMeta: (state, { payload }) => {
      const { assetId, properties, cycle, type } = payload;
      const spotDetailMeta = state.spotDetailMeta;
      spotDetailMeta[assetId] = {
        [type]: properties,
        cycle,
      };
    },
    setIntervalSpotTypes: (state, { payload }) => {
      state.intervalSpotTypes = payload;
    },
    updateSpotsLocation: (state, { payload }) => {
      const { assetId, spotState, location, updatedAt } = payload;
      const idx = state.spots.findIndex((spot) => spot.id === assetId);
      state.spots[idx].location = location;
      state.spots[idx].state = spotState;
      state.spots[idx].updatedAt = updatedAt;
      state.spots = [...state.spots];
    },
    updateAssetProperties: (state, { payload }) => {
      const { assetId, assetProperties } = payload;
      const idx = state.spots.findIndex((spot) => spot.id === assetId);
      if (idx !== -1) {
        state.spots[idx].assetProperties = assetProperties;
      }
    },
    updateSpots: (state, { payload }) => {
      // 対象のスポットタイプIDのスポットを一旦削除する
      const spots = state.spots.filter((spot) => {
        if (spot.assetTypeId !== undefined) {
          return payload.assetTypeIds.includes(spot.assetTypeId) === false;
        }

        return true;
      });
      state.spots = [...spots, ...payload.spots];
    },
    setLinkageLayerTypeIds: (state, { payload }) => {
      const spotTypeId = payload.spotTypeId;
      const layerTypeIds = payload.layerTypeIds;
      state.linkageLayerTypeIds[spotTypeId] = {
        layerTypeIds,
      };
    },
  },
});

export const {
  reducer: spotReducer,
  actions: {
    setMapTypeFlg,
    setSpots,
    setSpotTypes,
    setSpotTags,
    setGroupFlgs,
    setDefaultGroupFlgs,
    setMarkerMeta,
    updateSpotsLocation,
    setDetailMeta,
    updateAssetProperties,
    setIntervalSpotTypes,
    updateSpots,
    setLinkageLayerTypeIds,
  },
} = spotSlice;
